import {
    Button,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { TbTrash } from 'react-icons/tb';
import PartBayButton from '../PartBayButton';
import SearchableSingleSelect from '../SearchableSingleSelect';
import { SupplierSearchField } from '../SupplierSearchField';
import type { LocationEntry } from '../../hooks/useGetLocations';
import { LocationsPicker } from './UsersInlineCells';
import type { PriceMatchValue, ProhibitedVendor } from './InsuranceInlineCells';
import { toLocationList, type LocationList } from './locationListValue';
import { SettingsNumberInput } from './primitives';

/** Fields the DRP bulk bar can set across a selection. */
export type InsuranceBulkField = 'locations' | 'threshold' | 'priceMatch' | 'prohibitedVendors';

export interface InsuranceBulkValue {
    locations?: LocationList;
    /** null clears the threshold. */
    orderingThreshold?: number | null;
    priceMatch?: PriceMatchValue;
    /** Vendors to add to every selected carrier's existing list. */
    addProhibitedVendors?: ProhibitedVendor[];
}

export interface InsuranceBulkEditModalProps {
    field: InsuranceBulkField | null;
    selectedCount: number;
    locations: LocationEntry[];
    onApply: (value: InsuranceBulkValue) => void;
    onClose: () => void;
}

const FIELD_TITLE: Record<InsuranceBulkField, string> = {
    locations: 'Change locations',
    threshold: 'Change PartsTrader ordering threshold',
    priceMatch: 'Change price matching',
    prohibitedVendors: 'Add prohibited vendors',
};

/**
 * A custom ranking is per-carrier — it is built on the carrier's own screen — so
 * bulk price matching only offers the marketplace comparisons.
 */
const PRICE_MATCH_OPTIONS = [
    { value: 'estimate', label: 'Estimate' },
    { value: 'ccc', label: 'CCC Source Parts' },
    { value: 'partstrader', label: 'PartsTrader' },
    { value: 'both', label: 'CCC + PartsTrader' },
    { value: 'ccc+estimate', label: 'CCC + Estimate' },
    { value: 'partstrader+estimate', label: 'PartsTrader + Estimate' },
    { value: 'both+estimate', label: 'CCC + PartsTrader + Estimate' },
];

const priceMatchFromOption = (value: string): PriceMatchValue => ({
    estimate: value === 'estimate' || value.endsWith('+estimate'),
    ccc: value === 'ccc' || value === 'ccc+estimate' || value === 'both' || value === 'both+estimate',
    partstrader:
        value === 'partstrader' ||
        value === 'partstrader+estimate' ||
        value === 'both' ||
        value === 'both+estimate',
    custom: false,
});

/**
 * One value, applied to every selected DRP row. The bulk bar decides which
 * field is being changed; this only collects the new value and hands it back as
 * a settings fragment, matching how the users table's bulk editor works.
 */
const InsuranceBulkEditModal = ({
    field,
    selectedCount,
    locations,
    onApply,
    onClose,
}: InsuranceBulkEditModalProps) => {
    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>('all');
    const [threshold, setThreshold] = useState('');
    const [priceMatch, setPriceMatch] = useState('ccc');
    const [vendors, setVendors] = useState<ProhibitedVendor[]>([]);

    // Reset between openings so a previous pick never carries over.
    useEffect(() => {
        if (!field) return;
        setLocationIds('all');
        setThreshold('');
        setPriceMatch('ccc');
        setVendors([]);
    }, [field]);

    if (!field) return null;

    const thresholdError = threshold !== '' && !Number.isFinite(Number(threshold));
    const canApply =
        (field === 'locations' && (locationIds === 'all' || locationIds.length >= 0)) ||
        (field === 'threshold' && !thresholdError) ||
        field === 'priceMatch' ||
        (field === 'prohibitedVendors' && vendors.length > 0);

    const handleApply = () => {
        if (field === 'locations') {
            onApply({ locations: toLocationList(locationIds, locations) });
            return;
        }
        if (field === 'threshold') {
            onApply({ orderingThreshold: threshold === '' ? null : Number(threshold) });
            return;
        }
        if (field === 'priceMatch') {
            onApply({ priceMatch: priceMatchFromOption(priceMatch) });
            return;
        }
        onApply({ addProhibitedVendors: vendors });
    };

    return (
        <Modal isOpen onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold">
                    {FIELD_TITLE[field]}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Applies to {selectedCount} selected carrier
                            {selectedCount === 1 ? '' : 's'} and the sub companies on their
                            settings.
                        </Text>

                        {field === 'locations' && (
                            <LocationsPicker
                                value={locationIds}
                                locations={locations}
                                onChange={setLocationIds}
                                maxH="260px"
                            />
                        )}

                        {field === 'threshold' && (
                            <HStack spacing={3} align="center">
                                <SettingsNumberInput
                                    value={threshold}
                                    onChange={setThreshold}
                                    min={0}
                                    max={100}
                                    unit="%"
                                    width="110px"
                                    placeholder="—"
                                    ariaLabel="PartsTrader ordering threshold"
                                />
                                <Text fontSize="sm" color="gray.500">
                                    Leave blank to clear the threshold.
                                </Text>
                            </HStack>
                        )}

                        {field === 'priceMatch' && (
                            <VStack align="stretch" spacing={2}>
                                <SearchableSingleSelect
                                    width="100%"
                                    options={PRICE_MATCH_OPTIONS}
                                    value={priceMatch}
                                    onChange={setPriceMatch}
                                />
                                <Text fontSize="xs" color="gray.500">
                                    A custom ranking is set per carrier, so this replaces one with
                                    the marketplace comparison chosen here.
                                </Text>
                            </VStack>
                        )}

                        {field === 'prohibitedVendors' && (
                            <VStack align="stretch" spacing={2}>
                                <SupplierSearchField
                                    autoFocus
                                    placeholder="Search vendors to prohibit"
                                    emptyMessage="No vendors found"
                                    excludeIds={vendors.map((vendor) => vendor._id)}
                                    onSelect={(supplier) => {
                                        setVendors((prev) => [
                                            ...prev,
                                            { _id: supplier._id, title: supplier.title },
                                        ]);
                                    }}
                                />

                                {vendors.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500">
                                        No vendors chosen yet.
                                    </Text>
                                ) : (
                                    <VStack align="stretch" spacing={1}>
                                        {vendors.map((vendor) => (
                                            <HStack key={vendor._id} justify="space-between">
                                                <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                                    {vendor.title}
                                                </Text>
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    colorScheme="red"
                                                    leftIcon={
                                                        <Icon
                                                            as={TbTrash as React.ElementType}
                                                            boxSize={3.5}
                                                        />
                                                    }
                                                    onClick={() =>
                                                        setVendors((prev) =>
                                                            prev.filter(
                                                                (candidate) =>
                                                                    candidate._id !== vendor._id,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    Remove
                                                </Button>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                                <Text fontSize="xs" color="gray.500">
                                    These are added to whatever each carrier already prohibits.
                                </Text>
                            </VStack>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        isDisabled={!canApply}
                        onClick={handleApply}
                        text="Apply"
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InsuranceBulkEditModal;
